import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner, Button, Caption, List, Placeholder, Section, SegmentedControl, Spinner, Cell } from '@telegram-apps/telegram-ui';
import { useProducts } from '../hooks/useProducts';
import { ProductShootForm } from '../components/ProductShootForm';
import { useAgencyPlan, type AgencyIntent } from '../hooks/useAgencyPlan';
import { useCreateCatalogClean } from '../hooks/useGenerationMutations';

type Step = 'pick' | 'shoot';

const agencyModes: Array<{ id: AgencyIntent; label: string; title: string }> = [
  { id: 'catalog_clean', label: 'Карточка', title: 'Карточка товара' },
  { id: 'studio_model', label: 'Модель', title: 'Фото на модели' },
  { id: 'customer_tryon', label: 'Примерка', title: 'Примерка клиента' },
  { id: 'lifestyle_shoot', label: 'Lifestyle', title: 'Lifestyle-сессия' },
];

const roleLabels: Record<string, string> = {
  dispatcher: 'Диспетчер',
  retoucher: 'Ретушер',
  catalogizer: 'Каталогизатор',
  product_photographer: 'Предметный фотограф',
  fashion_photographer: 'Fashion-фотограф',
  stylist: 'Стилист',
  designer: 'Дизайнер',
  qa_inspector: 'QA',
};

export function StudioWizardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('pick');
  const [mode, setMode] = useState<AgencyIntent>('studio_model');
  const [productId, setProductId] = useState<string | null>(null);
  const [productName, setProductName] = useState('');
  const [catalogCleanProductId, setCatalogCleanProductId] = useState<string | null>(null);

  const q = useProducts({ limit: 50, status: 'active' });
  const agencyPlan = useAgencyPlan(mode);
  const createCatalogClean = useCreateCatalogClean();
  const items = q.data?.pages.flatMap(p => p.items) ?? [];
  const selectedMode = agencyModes.find(m => m.id === mode) ?? agencyModes[1];
  const primaryRoute = agencyPlan.data?.providerShortlist[0];

  const handleCatalogClean = async (selectedProductId: string) => {
    setCatalogCleanProductId(selectedProductId);
    try {
      const result = await createCatalogClean.mutateAsync({ productId: selectedProductId });
      navigate(`/generations/${result.id}`);
    } catch {
      /* haptic handled in mutation */
    } finally {
      setCatalogCleanProductId(null);
    }
  };

  if (step === 'shoot' && productId) {
    return (
      <List>
        <Section header={`${selectedMode.title}: ${productName}`}>
          <Button
            mode="plain"
            size="s"
            onClick={() => setStep('pick')}
          >
            ← Сменить товар
          </Button>
        </Section>
        <ProductShootForm
          productId={productId}
          intent={mode === 'lifestyle_shoot' ? 'lifestyle_shoot' : 'studio_model'}
          onDone={(genId) => navigate(`/generations/${genId}`)}
          onCancel={() => navigate('/generations')}
        />
      </List>
    );
  }

  if (q.isPending) return (
    <List>
      <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
        <Spinner size="m" />
      </div>
    </List>
  );

  return (
    <List>
      <Section header="AI-агентство">
        <SegmentedControl>
          {agencyModes.map(item => (
            <SegmentedControl.Item
              key={item.id}
              type="button"
              selected={mode === item.id}
              onClick={() => setMode(item.id)}
            >
              {item.label}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl>
        <Caption level="2" style={{ display: 'block', marginTop: 8 }}>
          {agencyPlan.isPending
            ? 'Собираем маршрут генерации...'
            : primaryRoute
              ? `${primaryRoute.provider.toUpperCase()} ${primaryRoute.model}, канва ${agencyPlan.data?.canonicalAspectRatio}`
              : 'Маршрут будет выбран автоматически'}
        </Caption>
      </Section>

      {agencyPlan.data && (
        <Section header="Команда и контроль качества">
          <Banner type="section">
            {agencyPlan.data.steps.map(stepItem => roleLabels[stepItem.role] ?? stepItem.role).join(' → ')}
          </Banner>
          {agencyPlan.data.qaRules.slice(0, 3).map(rule => (
            <Cell key={rule.id} subtitle={rule.severity === 'blocker' ? 'обязательная проверка' : 'предупреждение'}>
              {rule.label}
            </Cell>
          ))}
        </Section>
      )}

      {mode === 'catalog_clean' && (
        <Section header="Карточка товара">
          <Banner type="section">
            Выбери товар из каталога, и агентство сделает чистую карточку: товар по центру, белый фон, без модели и лишних деталей.
          </Banner>
          {createCatalogClean.isError && (
            <Banner type="section">
              Не удалось запустить карточку товара. Проверь фото товара и лимит примерок.
            </Banner>
          )}
          <Button stretched mode="filled" onClick={() => navigate('/catalog/analyze')}>
            Загрузить новый товар
          </Button>
          {items.length === 0 ? (
            <Placeholder
              header="Нет товаров"
              description="Сначала добавьте товар в Каталог"
              action={
                <Button onClick={() => navigate('/products/new')}>
                  Добавить товар
                </Button>
              }
            />
          ) : (
            items.map(p => (
              <Cell
                key={p.id}
                subtitle={catalogCleanProductId === p.id ? 'Запускаем...' : 'Создать clean card'}
                onClick={() => {
                  if (!createCatalogClean.isPending) void handleCatalogClean(p.id);
                }}
              >
                {p.name}
              </Cell>
            ))
          )}
        </Section>
      )}

      {mode === 'customer_tryon' && (
        <Section header="Примерка клиента">
          <Banner type="section">
            Для этого режима нужен товар и фото клиента. В рабочем флоу бот будет делать две версии: без улучшения фото и с мягкой ретушью.
          </Banner>
          <Button stretched mode="plain" onClick={() => navigate('/products')}>
            Выбрать товар в каталоге
          </Button>
        </Section>
      )}

      {(mode === 'studio_model' || mode === 'lifestyle_shoot') && (
        <Section header="Выбери товар для фотосессии">
        {items.length === 0 ? (
          <Placeholder
            header="Нет товаров"
            description="Сначала добавьте товар в Каталог"
            action={
              <Button onClick={() => navigate('/products/new')}>
                Добавить товар
              </Button>
            }
          />
        ) : (
          items.map(p => (
            <Cell
              key={p.id}
              onClick={() => {
                setProductId(p.id);
                setProductName(p.name);
                setStep('shoot');
              }}
            >
              {p.name}
            </Cell>
          ))
        )}
        </Section>
      )}
    </List>
  );
}
